import { useTranslation } from '@/i18n/useTranslation';

export interface BackLinkProps {
  readonly onClick: () => void;
}

/**
 * Back, as the design draws it: a 14px `--link`-coloured text button with a chevron, sitting in the card's
 * header strip.
 *
 * It is NOT the outlined pill KID-1 shipped. That mattered more than it looks — a pill reads as an action
 * of equal weight to Continue, and this control ends the candidate's session.
 *
 * ⚠️ **36px tall, which is BELOW the convention's 44px touch floor (§ 9), by decision.** The frame draws
 * 69 × 36 and the Operator chose the frame over the floor on 2026-08-13; the departure is recorded here and
 * raised to design rather than absorbed silently. It is the least bad control to shrink — it ends the
 * candidate's session, so a smaller target also means fewer accidental exits — but it is still a departure
 * from a citable rule on a device operated by a finger.
 */
export function BackLink({ onClick }: BackLinkProps) {
  const t = useTranslation();

  return (
    <button
      /*
        14/14 w600 in `--strong-foreground`, not 14/20 w400 in `--link`. Measured against `12446:28072`,
        `12021:41121` and `12055:10267` on 2026-09-08: the frames draw this near-black and semibold. It was
        teal and regular, which put the label 28px left of the frame at every width and — the part that
        matters — made a session-ending control look like a hyperlink.
      */
      className="kiosk-focusable flex h-9 items-center gap-1 p-2 text-sm font-semibold leading-[14px] text-[var(--strong-foreground)]"
      onClick={onClick}
      type="button"
    >
      {/*
        The 16px box and the 4 x 8 glyph inside it are kept SEPARATE, because the frame nests them: the
        icon box is 16 x 16, and the chevron within it is inset 37.5% horizontally and 25% vertically. An
        earlier build drew the asset at the box's 16px and the mark came out four times too wide — the same
        mistake made on the select's chevron, which is why it is spelled out here too.

        Drawn inline rather than loaded from `icon-chevron-left.svg`, because that asset hardcodes
        `stroke="#00808E"` and the frames stroke this chevron in the same near-black as the label. A colour
        baked into an asset cannot follow the control it belongs to; `currentColor` can, so the two can
        never disagree again.
      */}
      <span className="flex size-4 items-center justify-center">
        {/*
          `preserveAspectRatio="none"` is LOAD-BEARING, and dropping it was this repo's third chevron
          defect. The glyph is 4 x 8 in a 6 x 10 viewBox: uniform scaling takes min(4/6, 8/10) = 0.667,
          which draws the mark 4 x 6.67 — 17% short — on the ten screens that carry a Back control.
        */}
        <svg
          aria-hidden="true"
          className="h-2 w-1"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 6 10"
        >
          <path
            d="M5 9L1 5L5 1"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      </span>
      {t('checkIn.back')}
    </button>
  );
}
